import { getAddress, isAddress } from 'viem';
import { signTypedData } from 'wagmi/actions';

import {
    assertBridgeIntentMatchesIntent,
    sendBridgeIntentRegistration,
} from '@/features/custodial/services/bridgeIntent';
import { getPasskeyAssertionWithFriendlyError } from '@/features/tradingSession/passkeyAssertion';
import { readPasskeyOwnerHint } from '@/features/tradingSession/passkeyOwnerHint';
import { useTradingSessionStore } from '@/features/tradingSession/store';
import { getPasskeyAssertion } from '@/features/tradingSession/webauthn';
import { ensureTypedDataChain, walletWagmiConfig } from '@/features/wallet';
import {
    ANCHOR_CHAIN_IDS,
    DEFAULT_ANCHOR_CHAIN_ID,
    type AnchorChainId,
} from '@/features/wallet/config/anchors';
import {
    FundAuthorizationRequiredChallenge,
    SPEED_NETWORK_ID,
} from '@/lib/api/speed';
import type {
    BridgeIntentDescriptor,
    FundAuthorizationDescriptor,
    FundAuthorizationEntryWire,
    FundAuthorizationProofWire,
    FundAuthorizationsWire,
    TransferFees,
} from '@/lib/api/speed';
import { startPasskeyDiscovery } from '@/lib/api/tradingSession';

import {
    computeFundOperationChallenge,
    computeFundOperationsHash,
    createFundOperationTypedData,
    deriveFundOperationFields,
    mintFundOperationBinding,
    toFundOperationMemberWire,
} from './protocol';

/**
 * Minting ceremony for RustSigner's per-operation-set fund authorization. ONLY
 * the deposit, withdraw and migration flows go through this module — trading (open/close,
 * quotes, agent upkeep) uses the per-user agent-key signer path and must never
 * prompt, so nothing under `features/trading` may import from here.
 *
 * Flow contract with MoonHub.API (`SpeedTradingJobManager`): the FE opts into
 * the strict handshake by sending `fundAuthorizations: {}` on the first
 * `POST /speed/deposit|withdraw`. A prepare that still needs user signatures
 * answers HTTP 200 with `{ status: 'fundAuthorizationRequired', code:
 * 'FUND_AUTHORIZATION_REQUIRED', required: FundAuthorizationDescriptor[] }`
 * and burns nothing one-shot. The FE verifies the descriptors against the
 * user's intent and mints ONE proof over the whole ordered descriptor list —
 * a single signature commits to `operationsHash` (the batch commitment), and
 * every slot of the retry request carries that same proof with its own
 * `operationIndex`. RustSigner burns `(owner, nonce, index)` per sign call,
 * so each listed operation is admitted exactly once from one prompt (a
 * deposit's approve + withdraw pair no longer prompts twice). Each entry
 * still carries its own client-minted on-chain `operationExpiry` — the server
 * re-signs with EXACTLY that expiry, and RustSigner only admits the member
 * whose committed fields match the described values.
 */

/**
 * What the user is about to be asked for. `eip712` and `passkey` mint the fund
 * proof; `transaction` is the on-chain bridge-intent registration a withdrawal
 * sends from the connected wallet BEFORE the proof is minted, and it is
 * reported through the same `onPrompt` hook so the UI can say which prompt is
 * open.
 */
export type FundAuthorizationMethod = 'eip712' | 'passkey' | 'transaction';

export interface FundAuthorizationIntent {
    /** Connected wallet address, if a wallet is connected. */
    connectedAddress: string | null | undefined;
    /** Exact base-unit amount the user asked to move. */
    amount: bigint;
    /**
     * USDC (base units) the custodial wallet already holds on HyperEVM, read on chain by the
     * client. The server sweeps it into the deposit's HyperCore funding, so the hypercore slots may
     * exceed `amount` by exactly this much; anything beyond is still refused.
     */
    hyperCoreSweep?: bigint;
    flow: 'deposit' | 'withdrawal' | 'agent setup' | 'migration';
    /**
     * Migration only (and required there): the user's Snappy Wallet the V1
     * balance is paid to. The challenge's one `migrate_erc20` descriptor must
     * name exactly this address on HyperEVM, or nothing is signed.
     */
    migrationDestination?: string;
    /**
     * Resolves USDC on HyperEVM (the app's `resolveHyperUsdcAddress`, which
     * reads it from the backend rather than hardcoding it). Only a withdrawal
     * needs it, to check a bridge intent's token before the user registers it
     * on chain, and it is called ONLY when a challenge actually carries one —
     * so a flow that never bridges pays nothing for it. A challenge that
     * carries a bridge intent while this is missing or unresolvable is refused
     * rather than registered blindly.
     */
    resolveHyperUsdcAddress?: () => Promise<string | null | undefined>;
    /**
     * The authorization anchor this flow runs under: it picks the EIP-712 domain
     * the proof is signed in and the registry the bridge intent belongs to.
     * Absent means Cronos.
     */
    anchorChainId?: AnchorChainId;
    /** Called right before each user prompt opens. */
    onPrompt?: (method: FundAuthorizationMethod) => void;
    /**
     * Called with the bridge fee the backend quoted for THIS operation, the
     * moment a challenge states one and before anything is prompted or minted.
     *
     * The challenge is the only point where the real figure exists while the
     * user can still walk away: the route has been quoted, nothing has been
     * signed. Display only, and never load-bearing — a flow that ignores it, or
     * a backend that sends no fees, behaves exactly as before.
     */
    onFees?: (fees: TransferFees) => void;
}

/**
 * The honest dead end: a social-login user on a device with no connected
 * wallet and no enrolled passkey cannot authorize fund movement, by design.
 * There is deliberately no bypass — trading keeps working, money movement
 * waits for a device that can sign.
 */
export class FundAuthorizationUnavailableError extends Error {
    readonly code = 'fundAuthorizationUnavailable';

    constructor(flow: FundAuthorizationIntent['flow']) {
        super(
            flow === 'withdrawal'
                ? // a withdrawal also sends an on-chain registration from the
                // owner's own wallet, so a passkey cannot stand in for it here
                'Withdrawals need the wallet that owns this account ' +
                      'connected. Connect that wallet and try again. Trading ' +
                      'is not affected.'
                : flow === 'migration'
                    ? 'Migrating needs the wallet that owns this account ' +
                      'connected. Connect that wallet and try again. Trading ' +
                      'is not affected.'
                    : `This ${flow} needs an approval signature from the account ` +
                      'owner, and this device cannot produce one: no owner ' +
                      'wallet is connected and no passkey is enrolled here. ' +
                      'Connect the wallet that owns this account (or use the ' +
                      'device that has it), or add a passkey to your account ' +
                      'from a signed-in device first, then retry. Trading is ' +
                      'not affected.',
        );
        this.name = 'FundAuthorizationUnavailableError';
    }
}

export function isFundAuthorizationUnavailableError(
    error: unknown,
): error is FundAuthorizationUnavailableError {
    return (
        typeof error === 'object' &&
        error !== null &&
        'code' in error &&
        error.code === 'fundAuthorizationUnavailable'
    );
}

/**
 * Recognize a fund-authorization challenge and return its descriptors, or
 * `null` when the error is something else entirely. Shape validation already
 * happened where the challenge was raised
 * (`throwIfFundAuthorizationChallenge` in the speed API client) — a body that
 * claimed to be a challenge but did not validate never becomes this class.
 */
export function parseFundAuthorizationChallenge(
    error: unknown,
): FundAuthorizationDescriptor[] | null {
    return asFundAuthorizationChallenge(error)?.required ?? null;
}

/** The whole challenge (descriptors plus any bridge intent), or null. */
function asFundAuthorizationChallenge(
    error: unknown,
): FundAuthorizationRequiredChallenge | null {
    return error instanceof FundAuthorizationRequiredChallenge ? error : null;
}

function serverNowSeconds(): number {
    return Math.floor(
        useTradingSessionStore.getState().getServerNowMs(Date.now()) / 1000,
    );
}

function ownerCandidates(
    connectedAddress: string | null | undefined,
): string[] {
    const sessionStatus = useTradingSessionStore.getState().status;
    const sessionOwner =
        sessionStatus?.state === 'active' ? sessionStatus.owner : undefined;
    return [connectedAddress, sessionOwner, readPasskeyOwnerHint()]
        .filter((value): value is string => typeof value === 'string')
        .map((value) => value.toLowerCase());
}

/**
 * Chains a challenged fund operation may name: either authorization anchor (the
 * custodial wallet lives there) or HyperEVM (where trading funds move).
 */
const ALLOWED_NETWORK_IDS: readonly number[] = [
    ...ANCHOR_CHAIN_IDS,
    SPEED_NETWORK_ID,
];

/**
 * Plausibility bounds for the server-declared windows. The descriptor's
 * `maxAuthorizationSeconds` caps the on-chain expiry this client mints
 * (server policy is 300s for withdraws, 480s for deposit approvals); anything
 * outside these bounds is refused rather than signed.
 */
const MAX_AUTHORIZATION_HORIZON_SECONDS = 3600;
const MIN_AUTHORIZATION_SECONDS = 60;
const MIN_PROOF_WINDOW_SECONDS = 60;

/**
 * Skew margin subtracted from the minted `operationExpiry` so a client clock
 * ahead of the server's still lands inside `now + maxAuthorizationSeconds`.
 */
const OPERATION_EXPIRY_SKEW_MARGIN_SECONDS = 30;

/**
 * Slots whose `amount` is server-derived, so the signed value must be echoed
 * back in the entry (`FundAuthorizationEntry.amount` in
 * `MoonHub.API/access/ISignerAccess.ts` — today only the agent-setup
 * `bootstrapApprove` slot). Deposit/withdraw slots re-derive the amount from
 * the request and ignore an echo.
 */
const SERVER_DERIVED_AMOUNT_SLOTS: ReadonlySet<string> = new Set([
    'bootstrapApprove',
    'bootstrapFund',
]);

/**
 * Slots whose amount comes from the BRIDGE QUOTE, so it is legitimately LESS than the figure the
 * user typed.
 *
 * A deposit is bridged, and relay takes its fee out of the transfer: ask to move $10.00 and
 * ~$9.62 actually lands. The HyperCore funding leg is signed for what will actually arrive — it
 * used to be signed for the gross amount, which meant the custodial wallet never held enough and
 * `fund()` reverted with "transfer amount exceeds balance" forever.
 *
 * So an equality check against the user's figure rejects every legitimate deposit. What still has
 * to hold is the direction that protects the user: the amount may be smaller, never LARGER than
 * what they asked to move.
 */
const QUOTE_BOUNDED_AMOUNT_SLOTS: ReadonlySet<string> = new Set([
    'hypercoreApprove',
    'hypercoreFund',
    // withdrawal: the server caps the request to the live sweepable HyperCore balance (the FE asks
    // for the displayed balance, which can exceed it by the core-sweep reserve), so the hop debit
    // and the sweep ceiling are legitimately at most what the user asked for, never more.
    'hopWithdraw',
    'unifiedExit',
    // migration: the payout to the Snappy Wallet, capped by the server exactly like the hop debit.
    'migrateTransfer',
]);

/**
 * The unified sweep ceiling (to the user's own adapter, or to the legacy exit
 * wallet) is signed in HyperCore's 8-decimal USDC units while the user's
 * intent amount is the EVM/wire 6-decimal value, so that one slot legitimately
 * reads as intent.amount x 100.
 */
const CORE_8DP_SCALE = 100n;

function expectedDescriptorAmount(
    descriptor: FundAuthorizationDescriptor,
    intent: FundAuthorizationIntent,
): bigint {
    return descriptor.operation === 'hl_withdraw_unified_to_adapter'
        ? intent.amount * CORE_8DP_SCALE
        : intent.amount;
}

/**
 * The only operations a migration signs: the HyperCore sweep back to the
 * user's own adapter, and the one payout to their Snappy Wallet.
 */
const MIGRATION_OPERATIONS: ReadonlySet<string> = new Set([
    'hl_withdraw_unified_to_adapter',
    'migrate_erc20',
]);

/**
 * A migration pays out ONCE, on HyperEVM, to the Snappy Wallet the user picked
 * in the dialog, and it never bridges: anything else in the challenge (a
 * `withdraw_erc20` to some hop, a second payout, another chain, another
 * address) is refused before a prompt opens. Outside a migration a
 * `migrate_erc20` is refused outright, since nothing else ever pays one.
 */
function assertMigrationShape(
    descriptors: readonly FundAuthorizationDescriptor[],
    intent: FundAuthorizationIntent,
): void {
    if (intent.flow !== 'migration') {
        if (descriptors.some((d) => d.operation === 'migrate_erc20')) {
            throw new Error(
                `The server asked to authorize a migration payout during a ${intent.flow}; ` +
                    'refusing to sign.',
            );
        }
        return;
    }
    const destination = intent.migrationDestination;
    if (!destination || !isAddress(destination, { strict: false })) {
        throw new Error(
            'Could not tell which Snappy Wallet this migration pays; refusing to sign.',
        );
    }
    for (const descriptor of descriptors) {
        if (!MIGRATION_OPERATIONS.has(descriptor.operation)) {
            throw new Error(
                'The server asked to authorize something a migration does not do; ' +
                    'refusing to sign.',
            );
        }
    }
    const payouts = descriptors.filter((d) => d.operation === 'migrate_erc20');
    const [payout] = payouts;
    if (!payout || payouts.length !== 1) {
        throw new Error(
            'The server described a migration without exactly one payout; refusing to sign.',
        );
    }
    if (payout.chainId !== SPEED_NETWORK_ID) {
        throw new Error(
            'The server asked to pay the migration on an unexpected chain; refusing to sign.',
        );
    }
    if (payout.to?.toLowerCase() !== destination.toLowerCase()) {
        throw new Error(
            'The server asked to pay the migration to a different wallet than the one ' +
                'you picked; refusing to sign.',
        );
    }
}

/**
 * Refuse to sign anything that does not match what the user actually asked
 * for. RustSigner independently re-derives and enforces every signed value —
 * these checks exist so the USER is never prompted to approve an operation
 * for a different account or amount in the first place.
 */
export function assertFundOperationsMatchIntent(
    descriptors: readonly FundAuthorizationDescriptor[],
    intent: FundAuthorizationIntent,
): void {
    const candidates = ownerCandidates(intent.connectedAddress);
    if (candidates.length === 0) {
        throw new Error(
            'Could not verify which account this authorization is for. ' +
                'Reconnect your wallet or sign in again, then retry.',
        );
    }
    const [first] = descriptors;
    if (!first) {
        throw new Error('The server sent an empty fund authorization challenge.');
    }
    const owner = first.owner.toLowerCase();
    if (!candidates.includes(owner)) {
        throw new Error(
            'The server asked to authorize fund operations for a different ' +
                'account; refusing to sign.',
        );
    }
    assertMigrationShape(descriptors, intent);
    const seenKeys = new Set<string>();
    for (const descriptor of descriptors) {
        if (seenKeys.has(descriptor.key)) {
            // one proof is burned per slot; duplicate keys would collapse two
            // signatures into one map entry.
            throw new Error(
                'The server listed the same fund authorization slot twice; ' +
                    'refusing to sign.',
            );
        }
        seenKeys.add(descriptor.key);
        if (descriptor.owner.toLowerCase() !== owner) {
            throw new Error(
                'The server mixed multiple accounts into one fund ' +
                    'authorization; refusing to sign.',
            );
        }
        if (!ALLOWED_NETWORK_IDS.includes(descriptor.chainId)) {
            throw new Error(
                'The server asked to authorize a fund operation on an ' +
                    'unexpected chain; refusing to sign.',
            );
        }
        if (SERVER_DERIVED_AMOUNT_SLOTS.has(descriptor.key)) {
            // the server picks these from live on-chain state (the HyperCore bootstrap uses the
            // custodial wallet's actual balance), so the client cannot predict the value and an
            // equality check against intent.amount would reject every legitimate challenge. the owner,
            // chain and window bounds above still apply, RustSigner re-derives the amount itself, and
            // the user's proof commits to whatever value is echoed back — so the only thing we can and
            // should assert here is that it is a real, positive amount.
            if (BigInt(descriptor.amount) <= 0n) {
                throw new Error(
                    'The server described a server-derived fund operation with no amount; ' +
                        'refusing to sign.',
                );
            }
        } else if (QUOTE_BOUNDED_AMOUNT_SLOTS.has(descriptor.key)) {
            // bounded, not exempt: bridge fees only ever reduce what lands, so a larger figure than
            // the user asked to move is the thing worth refusing.
            const amount = BigInt(descriptor.amount);
            if (amount <= 0n) {
                throw new Error(
                    `The server described a ${intent.flow} with no amount; refusing to sign.`,
                );
            }
            const ceiling =
                expectedDescriptorAmount(descriptor, intent) + (intent.hyperCoreSweep ?? 0n);
            if (amount > ceiling) {
                throw new Error(
                    `The server asked to authorize MORE than the ${intent.flow} ` +
                        'amount requested; refusing to sign.',
                );
            }
        } else if (BigInt(descriptor.amount) !== expectedDescriptorAmount(descriptor, intent)) {
            throw new Error(
                `The server asked to authorize a different ${intent.flow} ` +
                    'amount than requested; refusing to sign.',
            );
        }
        if (
            descriptor.maxAuthorizationSeconds < MIN_AUTHORIZATION_SECONDS ||
            descriptor.maxAuthorizationSeconds >
                MAX_AUTHORIZATION_HORIZON_SECONDS ||
            descriptor.maxProofWindowSeconds < MIN_PROOF_WINDOW_SECONDS
        ) {
            throw new Error(
                'The server described a fund operation with an implausible ' +
                    'authorization window; refusing to sign.',
            );
        }
    }
}

/**
 * Pick the proof arm the same way the trading-session ceremony resolves the
 * user's authority: a passkey-backed session authorizes with the passkey (the
 * user may have no wallet on this device at all); otherwise the connected
 * owner wallet signs the EIP-712 payload; otherwise a previously used passkey
 * on this device is tried. A social-login user with none of these gets the
 * explicit {@link FundAuthorizationUnavailableError} instead of an opaque 403.
 */
function chooseMethod(
    owner: string,
    intent: FundAuthorizationIntent,
): 'eip712' | 'passkey' {
    const connected = intent.connectedAddress?.toLowerCase();

    // Withdrawals are wallet-only. The withdrawal also has the owner's EOA
    // register the bridge hop on chain, and a passkey cannot send that
    // transaction, so accepting a passkey proof here would only get the user
    // as far as a prompt they cannot complete. A migration is held to the
    // same rule: it is a withdrawal to a new address, and the connected owner
    // signs it (passkey proofs are refused server-side too).
    if (intent.flow === 'withdrawal' || intent.flow === 'migration') {
        if (connected === owner.toLowerCase()) return 'eip712';
        throw new FundAuthorizationUnavailableError(intent.flow);
    }

    const sessionStatus = useTradingSessionStore.getState().status;
    const sessionMethod =
        sessionStatus?.state === 'active' ? sessionStatus.method : null;
    if (sessionMethod === 'passkey') return 'passkey';

    if (connected === owner.toLowerCase()) return 'eip712';

    const hint = readPasskeyOwnerHint();
    if (hint && hint.toLowerCase() === owner.toLowerCase()) return 'passkey';

    throw new FundAuthorizationUnavailableError(intent.flow);
}

/**
 * The canonical RP id MoonHub enrolls passkeys under (the registrable domain,
 * not necessarily this page's host). Falls back to the browser default when
 * the discovery endpoint is unreachable.
 */
async function resolvePasskeyRpId(): Promise<string | undefined> {
    try {
        return (await startPasskeyDiscovery()).publicKey.rpId;
    } catch {
        return undefined;
    }
}

/**
 * The on-chain signature expiry this client mints for one slot: as much of
 * the server-declared `maxAuthorizationSeconds` budget as clock skew allows.
 * The same number goes into the signed digest AND the wire entry — the server
 * re-signs with exactly the echoed value.
 */
function mintOperationExpiry(
    nowSeconds: number,
    descriptor: FundAuthorizationDescriptor,
): number {
    return (
        nowSeconds +
        descriptor.maxAuthorizationSeconds -
        OPERATION_EXPIRY_SKEW_MARGIN_SECONDS
    );
}

/**
 * Mint one `FundAuthorizationEntry` per challenged descriptor, keyed by the
 * descriptor's `key` — the exact `fundAuthorizations` map the retry request
 * carries. The user signs ONCE: a single proof commits to the ordered
 * descriptor list via `operationsHash`, and every slot carries that proof
 * with its own `operationIndex` (RustSigner burns `(owner, nonce, index)`
 * per admitted member). Call {@link assertFundOperationsMatchIntent} first —
 * this function signs what it is given.
 */
export async function mintFundAuthorizationEntries(
    descriptors: readonly FundAuthorizationDescriptor[],
    intent: FundAuthorizationIntent,
): Promise<FundAuthorizationsWire> {
    const [first] = descriptors;
    if (!first) {
        throw new Error('The server sent an empty fund authorization challenge.');
    }
    const owner = getAddress(first.owner);
    const anchorChainId = intent.anchorChainId ?? DEFAULT_ANCHOR_CHAIN_ID;
    const method = chooseMethod(owner, intent);
    intent.onPrompt?.(method);

    const rpId = method === 'passkey' ? await resolvePasskeyRpId() : undefined;
    // one clock reading for the whole set: there is exactly one prompt, and
    // every member's expiry and the shared proof window hang off it.
    const now = serverNowSeconds();
    const seenKeys = new Set<string>();
    const expiries: number[] = [];
    const memberFields = descriptors.map((descriptor) => {
        if (seenKeys.has(descriptor.key)) {
            // assertFundOperationsMatchIntent already refuses duplicates;
            // this guard keeps "one slot per member" a local invariant.
            throw new Error(
                'The server listed the same fund authorization slot twice; ' +
                    'refusing to sign.',
            );
        }
        seenKeys.add(descriptor.key);
        const operationExpiry = mintOperationExpiry(now, descriptor);
        expiries.push(operationExpiry);
        return deriveFundOperationFields(descriptor, BigInt(operationExpiry));
    });
    const operations = memberFields.map(toFundOperationMemberWire);
    // the ONE binding every slot shares: its operationsHash is the batch
    // commitment over the ordered member list, and its window must satisfy
    // the tightest descriptor cap.
    const binding = mintFundOperationBinding(
        computeFundOperationsHash(memberFields),
        now,
        Math.min(
            ...descriptors.map(
                (descriptor) => descriptor.maxProofWindowSeconds,
            ),
        ),
    );

    let signProof: (operationIndex: number) => FundAuthorizationProofWire;
    if (method === 'eip712') {
        const typedData = createFundOperationTypedData(
            owner,
            binding,
            anchorChainId,
        );
        // the proof's domain is the anchor; the wallet may be sitting on any of
        // the three networks after an earlier step
        await ensureTypedDataChain(typedData);
        const signature = await signTypedData(walletWagmiConfig, {
            account: owner,
            ...typedData,
        });
        signProof = (operationIndex) => ({
            method: 'eip712',
            binding,
            operations,
            operationIndex,
            signature,
            anchorChainId,
        });
    } else {
        const challenge = await computeFundOperationChallenge(owner, binding);
        const assertion = await getPasskeyAssertionWithFriendlyError(
            getPasskeyAssertion,
            {
                challenge,
                ...(rpId ? { rpId } : {}),
                userVerification: 'required',
            },
        );
        signProof = (operationIndex) => ({
            method: 'passkey',
            binding,
            operations,
            operationIndex,
            anchorChainId,
            origin: window.location.origin,
            credentialId: assertion.rawId,
            authenticatorData: assertion.response.authenticatorData,
            clientDataJson: assertion.response.clientDataJSON,
            signature: assertion.response.signature,
        });
    }

    const entries: FundAuthorizationsWire = {};
    descriptors.forEach((descriptor, index) => {
        const entry: FundAuthorizationEntryWire = {
            proof: signProof(index),
            operationExpiry: String(expiries[index]),
        };
        if (SERVER_DERIVED_AMOUNT_SLOTS.has(descriptor.key)) {
            entry.amount = descriptor.amount;
        }
        entries[descriptor.key] = entry;
    });
    return entries;
}

/**
 * Extra request members a challenge round produced, handed to `run` alongside
 * the minted `fundAuthorizations` map. Today only a withdrawal fills this in:
 * the bridge intent its user just registered on chain.
 */
export interface FundAuthorizationExtras {
    bridgeIntent?: { id: string; txHash: string };
}

/**
 * A signed retry that comes back challenged again normally means the backend
 * re-derived different values, and looping would just burn more signatures. The
 * ONE exception is a withdrawal whose bridge hop was re-routed: the second
 * challenge then names a DIFFERENT bridge intent id, which is a real new thing
 * to authorize rather than a repeat of the one just refused. That is honoured
 * exactly once more.
 */
const MAX_CHALLENGE_ROUNDS = 2;

/**
 * Run a deposit/withdraw start request with the fund-authorization
 * challenge/retry handshake wrapped around it. The first attempt goes out
 * with an EMPTY `fundAuthorizations` map — that is what opts into the strict
 * handshake and makes the backend answer with descriptors (burning nothing)
 * instead of failing opaquely. Anything that is not a challenge passes
 * through untouched, so flows that are never challenged behave exactly as
 * before.
 *
 * A withdrawal challenge additionally carries the bridge hop the backend
 * quoted. That is checked against the user's own intent, registered on chain
 * from the connected wallet, and only THEN is the fund proof minted, so the
 * user never signs a proof for a hop they could not complete. The retry carries
 * both.
 */
export async function runWithFundAuthorization<T>(
    run: (
        fundAuthorizations: FundAuthorizationsWire,
        extras: FundAuthorizationExtras,
    ) => Promise<T>,
    intent: FundAuthorizationIntent,
): Promise<T> {
    const notAccepted = () =>
        new Error(
            `Your ${intent.flow} authorization was signed but not ` +
                'accepted. Please try again; if this keeps happening, ' +
                'contact support.',
        );

    let pending: FundAuthorizationRequiredChallenge;
    try {
        return await run({}, {});
    } catch (error) {
        const challenge = asFundAuthorizationChallenge(error);
        if (!challenge) throw error;
        pending = challenge;
    }

    let registeredBridgeIntentId: string | null = null;
    for (let round = 0; round < MAX_CHALLENGE_ROUNDS; round += 1) {
        const challenge = pending;
        // FIRST, before any prompt opens and before a single proof is minted:
        // what this is going to cost. A re-routed hop re-quotes, so a later
        // round can state a different figure and the caller hears that too.
        if (challenge.fees) {
            intent.onFees?.(challenge.fees);
        }
        // a migration never bridges, so a challenge that asks for a bridge
        // registration is describing some other operation.
        if (intent.flow === 'migration' && challenge.bridgeIntent) {
            throw new Error(
                'The server asked to register a bridge for a migration, which never ' +
                    'bridges; refusing to sign.',
            );
        }
        const bridgeIntent = bridgeIntentToRegister(
            challenge,
            intent,
            registeredBridgeIntentId,
        );
        // a repeat challenge that brings nothing new to authorize is the end of
        // the line, whatever it is repeating.
        if (round > 0 && !bridgeIntent) throw notAccepted();

        assertFundOperationsMatchIntent(challenge.required, intent);

        const extras: FundAuthorizationExtras = {};
        if (bridgeIntent) {
            assertBridgeIntentMatchesIntent(bridgeIntent, {
                connectedAddress: intent.connectedAddress,
                amount: intent.amount,
                hyperUsdcAddress: await intent.resolveHyperUsdcAddress?.(),
                anchorChainId: intent.anchorChainId ?? DEFAULT_ANCHOR_CHAIN_ID,
            });
            intent.onPrompt?.('transaction');
            const txHash = await sendBridgeIntentRegistration(
                bridgeIntent,
                intent.anchorChainId ?? DEFAULT_ANCHOR_CHAIN_ID,
            );
            registeredBridgeIntentId = bridgeIntent.id.toLowerCase();
            extras.bridgeIntent = { id: bridgeIntent.id, txHash };
        }

        const entries = await mintFundAuthorizationEntries(
            challenge.required,
            intent,
        );
        try {
            return await run(entries, extras);
        } catch (retryError) {
            const next = asFundAuthorizationChallenge(retryError);
            if (!next) throw retryError;
            pending = next;
        }
    }
    throw notAccepted();
}

/**
 * The bridge hop this round has to register, or null when there is nothing new:
 * a flow that does not bridge, a backend that predates the registry, or a
 * repeat of the id the user already registered.
 */
function bridgeIntentToRegister(
    challenge: FundAuthorizationRequiredChallenge,
    intent: FundAuthorizationIntent,
    registeredId: string | null,
): BridgeIntentDescriptor | null {
    if (intent.flow !== 'withdrawal') return null;
    const descriptor = challenge.bridgeIntent;
    if (!descriptor) return null;
    return descriptor.id.toLowerCase() === registeredId ? null : descriptor;
}
